import { useMemo } from 'react';
import {
  ClipboardList,
  Wrench,
  DollarSign,
  Receipt,
  Clock,
  Star,
  Users,
  ParkingSquare,
  CreditCard,
} from 'lucide-react';
import { motion } from 'framer-motion';
import { useAuth } from '@/hooks/useAuth';
import { useDashboard } from '@/hooks/useDashboard';
import { useInventoryAlerts } from '@/hooks/useInventory';
import { useScheduleToday } from '@/hooks/useMechanicHR';
import { ErrorState, KPICard } from '@mustauto/ui/web';
import { OSChart } from '@/components/OSChart';
import { RecentOrdersTable } from '@/components/RecentOrdersTable';
import { ConnectionIndicator } from '@/components/ConnectionIndicator';
import { AttentionSection, type AttentionItem } from '@/components/AttentionSection';
import { NextStepsCard } from '@/components/NextStepsCard';
import { PaintBoothCard } from '@/components/PaintBoothCard';
import { ReferralCard } from '@/components/ReferralCard';
import { ReputationCard } from '@/components/ReputationCard';
import { SkeletonKPIGrid, SkeletonChart, SkeletonTable } from '@mustauto/ui/web';
import { formatReais, formatNumber } from '@/lib/utils';
import { formatDuration, formatElapsed } from '@mustauto/utils';
import { getStageLabel } from '@mustauto/types';
import { useStageConfig } from '@/hooks/useSettings';
import { useCan } from '@/hooks/useCan';

export function DashboardPage() {
  const { user } = useAuth();
  const canSeeRevenue = useCan()('painel.faturamento');
  const { data, isLoading, isError, error, refetch, wsStatus } = useDashboard(user?.workshopId ?? null);
  const { alerts, alertCount } = useInventoryAlerts();
  const { data: scheduleToday } = useScheduleToday();
  const stageConfig = useStageConfig();

  // Build attention items from existing data
  const attentionItems = useMemo<AttentionItem[]>(() => {
    if (!data) return [];
    const items: AttentionItem[] = [];
    const { kpis } = data;

    if (alertCount > 0) {
      items.push({
        id: 'inventory-low',
        severity: 'critical',
        message: `${alertCount} ${alertCount === 1 ? 'peça abaixo' : 'peças abaixo'} do estoque mínimo`,
        action: { label: 'Ver estoque', href: '/inventory' },
      });
    }

    if (kpis.waitingPayment > 0) {
      items.push({
        id: 'waiting-payment',
        severity: 'warning',
        message: `${kpis.waitingPayment} OS aguardando pagamento`,
        action: { label: 'Ver OS', href: '/service-orders?status=waiting_payment' },
      });
    }

    // OS paradas. A conta é do servidor, com o critério da tela de alertas do
    // app e do resumo da manhã: o limiar da oficina, ou o do tipo da OS
    // (funilaria em dias). Antes saía daqui, sobre `recentOrders` — que é só o
    // DIA —, com "criada há mais de 24h": nenhuma OS de hoje tem 24h, e o aviso
    // nunca acendia. E "há +24h" ficaria errado de novo para quem é funilaria.
    const staleCount = data.staleOsCount ?? 0;
    if (staleCount > 0) {
      items.push({
        id: 'stale-orders',
        severity: 'warning',
        message: `${staleCount} ${staleCount === 1 ? 'OS parada' : 'OS paradas'} sem movimento`,
        action: { label: 'Ver OS', href: '/service-orders?stale=true' },
      });
    }

    // Etapa atrasada (F3.7): uma linha por OS, porque cada uma pede uma
    // cobrança diferente ("a pintura do ABC passou 5 h"). Só existe para quem
    // configurou o tempo das etapas.
    for (const o of data.overdueStages ?? []) {
      items.push({
        id: `stage-late-${o.id}`,
        severity: 'warning',
        message: `OS #${o.number} · ${o.plate} — ${getStageLabel(o.stage, stageConfig)} atrasada ${formatDuration(Date.now() - new Date(o.expectedBy).getTime())}`,
        action: { label: 'Abrir OS', href: `/service-orders/${o.id}` },
      });
    }
    const hiddenLate = (data.overdueStageCount ?? 0) - (data.overdueStages?.length ?? 0);
    if (hiddenLate > 0) {
      items.push({
        id: 'stage-late-more',
        severity: 'warning',
        message: `E mais ${hiddenLate} OS com etapa atrasada`,
        action: { label: 'Ver OS', href: '/service-orders?status=in_execution' },
      });
    }

    // Funilaria esperando peça (F3.7): saiu das "paradas" — o gestor não tem
    // o que fazer além de esperar —, mas não some: o mais antigo diz a quem
    // cobrar.
    const waiting = data.waitingPart;
    if (waiting && waiting.count > 0) {
      const oldest = waiting.oldestSince ? formatElapsed(waiting.oldestSince) : null;
      items.push({
        id: 'waiting-part',
        severity: 'info',
        message: `${waiting.count} ${waiting.count === 1 ? 'carro de funilaria aguardando peça' : 'carros de funilaria aguardando peça'}${oldest ? ` — o mais antigo ${oldest}` : ''}`,
        action: { label: 'Ver OS', href: '/service-orders?status=waiting_part' },
      });
    }

    return items;
  }, [data, alertCount, stageConfig]);

  if (isError && !data) {
    return <ErrorState error={error} onRetry={() => refetch()} />;
  }

  if (isLoading || !data) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="h-7 w-40 rounded bg-muted-foreground/10 animate-pulse" />
            <div className="h-4 w-64 rounded bg-muted-foreground/10 animate-pulse mt-2" />
          </div>
        </div>
        <SkeletonKPIGrid />
        <SkeletonChart />
        <SkeletonTable />
      </div>
    );
  }

  const { kpis, recentOrders, chartData } = data;

  return (
    <motion.div
      className="space-y-6"
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Dashboard</h1>
          <p className="text-sm text-muted-foreground">
            Visão geral da oficina em tempo real
          </p>
        </div>
        <ConnectionIndicator status={wsStatus} />
      </div>

      {/* Próximos passos — continuidade do onboarding */}
      <NextStepsCard
        workshopId={user?.workshopId ?? null}
        hasSlots={kpis.totalSlots > 0}
        // `recentOrders` é só o DIA: lendo dali, a oficina com 500 OS e uma
        // manhã quieta via "Abrir sua primeira OS" desmarcado, com a barra de
        // progresso andando para trás e o card ressuscitando todo dia.
        hasFirstOS={data.hasAnyOrder}
      />

      {/* KPI grid */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <KPICard
          title="OS Abertas Hoje"
          value={formatNumber(kpis.openToday)}
          icon={<ClipboardList size={20} />}
        />
        <KPICard
          title="OS Em Execução"
          value={formatNumber(kpis.inExecution)}
          icon={<Wrench size={20} />}
        />
        {/* Dinheiro: quem o gestor tirou "Ver o faturamento" não vê (Fase 3). */}
        {canSeeRevenue && (
          <>
            <KPICard
              title="Faturado Hoje"
              value={formatReais(kpis.revenueToday)}
              icon={<DollarSign size={20} />}
            />
            <KPICard
              title="A Receber"
              value={formatReais(kpis.receivableTotal)}
              icon={<Receipt size={20} />}
            />
            <KPICard
              title="Faturamento do Mês"
              value={formatReais(kpis.monthRevenue)}
              icon={<DollarSign size={20} />}
            />
            <KPICard
              title="Ticket Médio"
              value={formatReais(kpis.avgTicket)}
              icon={<Receipt size={20} />}
            />
          </>
        )}
        <KPICard
          title="Tempo Médio de Execução"
          value={`${kpis.avgExecutionTime.toFixed(1)}h`}
          icon={<Clock size={20} />}
        />
        <KPICard
          title="Avaliação / NPS"
          value={
            kpis.avgRating !== null
              ? `${kpis.avgRating.toFixed(1)} / NPS ${kpis.nps !== null && kpis.nps >= 0 ? '+' : ''}${kpis.nps ?? '—'}`
              : '—'
          }
          icon={<Star size={20} />}
          subtitle={kpis.avgRating === null ? 'Aguardando avaliações' : undefined}
        />
        <KPICard
          title="Mecânicos Ativos"
          value={
            scheduleToday
              ? `${scheduleToday.activeToday} de ${scheduleToday.scheduledToday}`
              : '—'
          }
          icon={<Users size={20} />}
          subtitle={
            scheduleToday && scheduleToday.absentToday > 0
              ? `${scheduleToday.absentToday} ausente${scheduleToday.absentToday !== 1 ? 's' : ''}`
              : undefined
          }
        />
        <KPICard
          title="Vagas Livres"
          value={
            kpis.totalSlots > 0
              ? `${kpis.freeSlots} de ${kpis.totalSlots}`
              : '—'
          }
          icon={<ParkingSquare size={20} />}
          subtitle={kpis.totalSlots === 0 ? 'Nenhuma vaga cadastrada' : undefined}
        />
        <KPICard
          title="Aguardando Pagamento"
          value={formatNumber(kpis.waitingPayment)}
          icon={<CreditCard size={20} />}
        />
      </div>

      {/* Attention section */}
      <AttentionSection items={attentionItems} />

      {/* Cabine de pintura (F3.5) — só com o módulo de funilaria */}
      <PaintBoothCard workshopId={user?.workshopId ?? null} />

      {/* Selo MustAuto Verificada — reputação da oficina */}
      <ReputationCard workshopId={user?.workshopId ?? null} />

      {/* Chart */}
      <OSChart data={chartData} />

      {/* Table */}
      <RecentOrdersTable orders={recentOrders} />

      {/* Indicação oficina→oficina (growth 8.1) */}
      <ReferralCard workshopId={user?.workshopId ?? null} />
    </motion.div>
  );
}
